// Hook: manage active conversation + messages (SPEC 001 T146)
import { useCallback, useState } from "react";
import {
  CopilotConversation,
  CopilotMessage,
  CopilotToolCallPreviewViewModel,
} from "../../../interfaces/Copilot";
import {
  createConversation,
  deleteConversation,
  editMessage,
  getConversation,
  sendMessage,
} from "../../../services/copilot/copilotApi";

interface UseCopilotSessionReturn {
  conversation: CopilotConversation | null;
  messages: CopilotMessage[];
  sending: boolean;
  quotaRemaining: number | null;
  pendingToolCalls: CopilotToolCallPreviewViewModel[];
  send: (content: string) => Promise<void>;
  editAndResend: (messageId: string, content: string) => Promise<void>;
  newConversation: () => void;
  clearConversation: () => Promise<void>;
  refresh: () => Promise<void>;
  setPendingToolCalls: (tcs: CopilotToolCallPreviewViewModel[]) => void;
  appendMessages: (msgs: CopilotMessage[]) => void;
}

export function useCopilotSession(): UseCopilotSessionReturn {
  const [conversation, setConversation] =
    useState<CopilotConversation | null>(null);
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [quotaRemaining, setQuotaRemaining] = useState<number | null>(null);
  const [pendingToolCalls, setPendingToolCallsState] =
    useState<CopilotToolCallPreviewViewModel[]>([]);

  const ensureConversation =
    useCallback(async (): Promise<CopilotConversation> => {
      if (conversation) return conversation;
      const newConv = await createConversation();
      setConversation(newConv);
      return newConv;
    }, [conversation]);

  const send = useCallback(
    async (content: string) => {
      setSending(true);
      try {
        const conv = await ensureConversation();
        const turn = await sendMessage(conv.id, { content });
        setMessages((prev) => [...prev, ...turn.newMessages]);
        setPendingToolCallsState(turn.pendingToolCalls);
        setQuotaRemaining(turn.quotaRemaining);
      } catch {
        // errors surfaced by callers or error boundary
      } finally {
        setSending(false);
      }
    },
    [ensureConversation]
  );

  const editAndResend = useCallback(
    async (messageId: string, content: string) => {
      if (!conversation) return;
      setSending(true);
      try {
        const turn = await editMessage(conversation.id, messageId, content);
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === messageId);
          const kept = idx >= 0 ? prev.slice(0, idx) : prev;
          return [...kept, ...turn.newMessages];
        });
        setPendingToolCallsState(turn.pendingToolCalls);
        setQuotaRemaining(turn.quotaRemaining);
      } catch {
        // errors surfaced by callers or error boundary
      } finally {
        setSending(false);
      }
    },
    [conversation]
  );

  const newConversation = useCallback(() => {
    setConversation(null);
    setMessages([]);
    setPendingToolCallsState([]);
  }, []);

  const clearConversation = useCallback(async () => {
    if (conversation) {
      try {
        await deleteConversation(conversation.id);
      } catch {
        // swallow — reset locally regardless
      }
    }
    setConversation(null);
    setMessages([]);
    setPendingToolCallsState([]);
  }, [conversation]);

  const refresh = useCallback(async () => {
    if (!conversation) return;
    try {
      const detail = await getConversation(conversation.id);
      setMessages(detail.messages);
      setPendingToolCallsState(detail.pendingToolCalls);
    } catch {
      // swallow
    }
  }, [conversation]);

  const setPendingToolCalls = useCallback(
    (tcs: CopilotToolCallPreviewViewModel[]) => {
      setPendingToolCallsState(tcs);
    },
    []
  );

  const appendMessages = useCallback((msgs: CopilotMessage[]) => {
    setMessages((prev) => [...prev, ...msgs]);
  }, []);

  return {
    conversation,
    messages,
    sending,
    quotaRemaining,
    pendingToolCalls,
    send,
    editAndResend,
    newConversation,
    clearConversation,
    refresh,
    setPendingToolCalls,
    appendMessages,
  };
}
